import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Headphones,
  Mail,
  MessageSquare,
  Phone,
  FileText,
  ShoppingBag,
  UserPlus,
  BarChart3,
  Wrench,
  CheckCircle2,
  ArrowRight,
  Clock,
  DollarSign,
  Users,
  Globe
} from 'lucide-react';

const OutsourceSupport = () => {
  const industries = [
    "E-commerce", "SaaS", "Fintech", "Healthcare",
    "Education", "Travel", "Telecom", "Logistics"
  ];

  const benefits = [
    { icon: Clock, title: "24/7 Support", desc: "Round-the-clock availability" },
    { icon: DollarSign, title: "Cost Savings", desc: "Up to 60% cost reduction" },
    { icon: Users, title: "Dedicated Team", desc: "Trained professionals" },
    { icon: Globe, title: "Scalable Resources", desc: "Flexible team sizing" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-4">Digital Jockey / Outsource Support</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Scale Your Business with{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Expert Support Teams
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Professional customer support, back office operations, lead generation, 
            and technical support services to help you grow faster.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
              Hire Support Team
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="customer-support" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-12 bg-gray-100 p-2 rounded-lg">
              <TabsTrigger value="customer-support" className="data-[state=active]:bg-white data-[state=active]:text-blue-600">
                Customer Support
              </TabsTrigger>
              <TabsTrigger value="back-office" className="data-[state=active]:bg-white data-[state=active]:text-blue-600">
                Back Office
              </TabsTrigger>
              <TabsTrigger value="lead-generation" className="data-[state=active]:bg-white data-[state=active]:text-blue-600">
                Lead Generation
              </TabsTrigger>
              <TabsTrigger value="tech-support" className="data-[state=active]:bg-white data-[state=active]:text-blue-600">
                Tech Support
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customer-support">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Mail className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Support</h3>
                  <p className="text-gray-600 mb-4">Professional email response handling with quick turnaround times</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">24-hour response time</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Multi-language support</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <MessageSquare className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Live Chat Support</h3>
                  <p className="text-gray-600 mb-4">Real-time customer assistance for instant problem resolution</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Real-time responses</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Multi-chat handling</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Phone className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Voice Support</h3>
                  <p className="text-gray-600 mb-4">Inbound and outbound call handling by trained professionals</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Inbound/Outbound calls</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Call quality monitoring</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Headphones className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Social Media Support</h3>
                  <p className="text-gray-600 mb-4">Multi-channel social media customer engagement</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Facebook, Twitter, Instagram</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Quick response times</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="back-office">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <FileText className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Data Entry</h3>
                  <p className="text-gray-600 mb-4">Accurate and efficient data processing services</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">99.9% accuracy guaranteed</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <ShoppingBag className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Order Processing</h3>
                  <p className="text-gray-600 mb-4">Efficient order management and fulfillment</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Fast processing times</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <UserPlus className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Virtual Assistant</h3>
                  <p className="text-gray-600 mb-4">Administrative support services for your business</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Calendar & email management</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <FileText className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">CRM Updates</h3>
                  <p className="text-gray-600 mb-4">Database maintenance and CRM management</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Regular data updates</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="lead-generation">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <BarChart3 className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">B2B Lead Generation</h3>
                  <p className="text-gray-600 mb-4">Quality business leads for your sales team</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Targeted lead sourcing</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Phone className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Appointment Setting</h3>
                  <p className="text-gray-600 mb-4">Professional calendar and meeting management</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Qualified appointments</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Users className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Sales Support</h3>
                  <p className="text-gray-600 mb-4">Comprehensive sales team assistance</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Follow-up management</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <BarChart3 className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Market Research</h3>
                  <p className="text-gray-600 mb-4">Industry insights and competitor analysis</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Detailed reports</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="tech-support">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Headphones className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Helpdesk Services</h3>
                  <p className="text-gray-600 mb-4">First-line IT support for your users</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Ticket management system</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Wrench className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">L1/L2 Support</h3>
                  <p className="text-gray-600 mb-4">Multi-tier technical support services</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Escalation management</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Phone className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Remote Troubleshooting</h3>
                  <p className="text-gray-600 mb-4">Quick issue resolution and support</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Remote desktop support</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 border-2 hover:border-blue-200 transition-all duration-300">
                  <Globe className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">SaaS Support</h3>
                  <p className="text-gray-600 mb-4">Cloud platform assistance and support</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Platform expertise</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry, index) => (
              <Badge 
                key={index} 
                className="px-6 py-3 text-base bg-white text-gray-700 border-2 border-gray-200"
              >
                {industry}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Outsource to Us?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-8 text-center border-2 hover:border-blue-200 transition-all duration-300">
                <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Scale Your Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss your outsourcing needs and build a custom solution
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OutsourceSupport;
